import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { PhoneInput, isValidUSPhone } from '../../components/ui/PhoneInput';
import locationService, { CreateLocationRequest } from '../../services/api/locationService';
import { LocationForm, LocationFieldRow, LocationFormError, COUNTRY } from './LocationFormLayout';
import { CityAutocomplete } from './CityAutocomplete';
import { useStateOptions } from './useStateOptions';

interface CreateFormValues {
  name: string;
  street: string;
  city: string;
  state: string;      // full state name, e.g. "California"
  stateCode: string;  // used for city lookup only, not submitted
  zip: string;
  phone: string;
}

interface CreateLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateLocationModal = ({ isOpen, onClose }: CreateLocationModalProps) => {
  const queryClient = useQueryClient();
  const { stateOptions, isLoading: statesLoading, resolveStateName } = useStateOptions();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<CreateFormValues>({
    defaultValues: {
      name: '',
      street: '',
      city: '',
      state: '',
      stateCode: '',
      zip: '',
      phone: '',
    },
  });

  const stateCode = watch('stateCode');

  const createMutation = useMutation({
    mutationFn: (data: CreateLocationRequest) => locationService.createLocation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      handleClose();
    },
  });

  const onSubmit = ({ stateCode: _, ...values }: CreateFormValues) => {
    createMutation.mutate({ ...values, country: COUNTRY });
  };

  const handleClose = () => {
    reset();
    createMutation.reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Center"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary-blue"
            onClick={handleSubmit(onSubmit)}
            isLoading={createMutation.isPending}
          >
            Create Center
          </Button>
        </>
      }
    >
      <LocationForm onSubmit={handleSubmit(onSubmit)}>
        {createMutation.isError && (
          <LocationFormError>
            {(createMutation.error as any)?.response?.data?.message ||
              'Failed to create center. Please try again.'}
          </LocationFormError>
        )}

        <Input
          {...register('name', { required: 'Name is required' })}
          label="Center Name"
          placeholder="e.g. Phoenix North Clinic"
          error={errors.name?.message}
          required
          fullWidth
        />

        <Input
          {...register('street')}
          label="Street Address"
          placeholder="123 Main St"
          fullWidth
        />

        <LocationFieldRow>
          <Controller
            name="city"
            control={control}
            rules={{ required: 'City is required' }}
            render={({ field, fieldState }) => (
              <CityAutocomplete
                stateCode={stateCode}
                value={field.value}
                onChange={field.onChange}
                error={fieldState.error?.message}
                disabled={!stateCode}
              />
            )}
          />

          <Controller
            name="stateCode"
            control={control}
            rules={{ required: 'State is required' }}
            render={({ field, fieldState }) => (
              <Select
                label="State"
                fullWidth
                required
                disabled={statesLoading}
                options={stateOptions}
                value={field.value}
                error={fieldState.error?.message}
                onChange={(e) => {
                  const code = e.target.value;
                  field.onChange(code);
                  setValue('state', resolveStateName(code));
                  setValue('city', '');
                }}
              />
            )}
          />
        </LocationFieldRow>

        <LocationFieldRow>
          <Input
            {...register('zip')}
            label="ZIP Code"
            placeholder="85001"
            fullWidth
          />

          <Input
            label="Country"
            value={COUNTRY}
            readOnly
            disabled
            fullWidth
          />
        </LocationFieldRow>

        <Controller
          name="phone"
          control={control}
          rules={{
            validate: (v) => isValidUSPhone(v) || 'Enter a valid 10-digit US phone number',
          }}
          render={({ field, fieldState }) => (
            <PhoneInput
              label="Phone Number"
              fullWidth
              value={field.value}
              error={fieldState.error?.message}
              onChange={field.onChange}
            />
          )}
        />
      </LocationForm>
    </Modal>
  );
};
